import React, { useEffect, useReducer, useRef, useState } from "react";

import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import FormGroup from "@mui/material/FormGroup"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"

import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { findIndex, isEqual, isNull, orderBy } from "lodash"

import BODService from "@plannel/services/data/bod-service";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";

const INITIAL_STATE = { originLocation: null, destLocation: null };
const FILTER_ORIGIN_LOCATION = "filterOriginLocation";
const FILTER_DEST_LOCATION = "filterDestLocation";

const reducer = (state, action) => {
  const value = action.value;

  switch (action.type) {
    case FILTER_ORIGIN_LOCATION:
      return {
        ...state, originLocation: value
      }
    case FILTER_DEST_LOCATION:
      return {
        ...state, destLocation: value
      }
    default:
      return {
        ...state
      }
  }
}

const OriginDestLocationAutoComplete = props => {
  const { viewName, onChangeFilter, reload, setReload, sx } = props;

  const { t } = useTranslation();

  const reduxDispatch = useDispatch();
  const reduxViewState = reduxUtil.getViewState(viewName);

  const [loading, setLoading] = useState(false);
  const [filterValue, dispatch] = useReducer(reducer, INITIAL_STATE);

  const bodLookupList = useRef([]);
  const allDestLocations = useRef([]);
  const [originLocations, setOriginLocations] = useState([]);
  const [destLocations, setDestLocations] = useState([]);

  useEffect(() => {
    if (loading) {
      onChangeFilter(filterValue);

      const reduxState = reduxUtil.createObjectState(viewName, { locationSearch: filterValue });
      reduxDispatch(reduxUtil.updateViewState(reduxState));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue, loading]);

  const setFilteredDestLocations = (results, originLocation, destLocation) => {
    const originLocationMap = new Map();
    const destLocationMap = new Map();
    const allDestLocationMap = new Map();

    for (const result of results) {
      if (!originLocationMap.has(result.originLocation?.locCd)) {
        originLocationMap.set(result.originLocation.locCd, result.originLocation);
      }

      if (!destLocationMap.has(result.destLocation?.locCd)) {
        allDestLocationMap.set(result.destLocation.locCd, result.destLocation);
        if (isNull(originLocation)) {
          destLocationMap.set(result.destLocation?.locCd, result.destLocation);
        } else {
          if (isEqual(result.originLocation.locCd, originLocation.locCd)) {
            destLocationMap.set(result.destLocation?.locCd, result.destLocation);
          }
        }
      }
    }

    const originLocationList = orderBy(Array.from(originLocationMap.values()), ["name"], ["asc"]);
    const destLocationList = orderBy(Array.from(destLocationMap.values()), ["name"], ["asc"]);
    const allDestLocationList = orderBy(Array.from(allDestLocationMap.values()), ["name"], ["asc"]);

    setOriginLocations(originLocationList);
    setDestLocations(destLocationList);

    allDestLocations.current = allDestLocationList;

    if (originLocation?.locCd) {
      const originIdx = findIndex(originLocationList, { locCd: originLocation?.locCd });
      if (originIdx < 0) {
        dispatch({ type: FILTER_ORIGIN_LOCATION, value: null });
      }
    }

    if (destLocation?.locCd) {
      const destIdx = findIndex(destLocationList, { locCd: destLocation?.locCd });
      if (destIdx < 0) {
        dispatch({ type: FILTER_DEST_LOCATION, value: null });
      }
    }

    setLoading(true);
  }

  const retrieveOriginDestLocations = () => {
    BODService.getLookup()
      .then(response => {
        const { results } = response.data;

        bodLookupList.current = results;
        if (reduxViewState?.locationSearch) {
          const reduxValue = { ...filterValue };
          if (Object.prototype.hasOwnProperty.call(reduxViewState.locationSearch, "originLocation")) {
            reduxValue.originLocation = reduxViewState.locationSearch.originLocation;
            dispatch({ type: FILTER_ORIGIN_LOCATION, value: reduxValue.originLocation });
          }

          if (Object.prototype.hasOwnProperty.call(reduxViewState.locationSearch, "destLocation")) {
            reduxValue.destLocation = reduxViewState.locationSearch.destLocation;
            dispatch({ type: FILTER_DEST_LOCATION, value: reduxValue.destLocation });
          }

          setFilteredDestLocations(results, reduxValue.originLocation, reduxValue.destLocation);
        } else {
          setFilteredDestLocations(results, filterValue.originLocation, filterValue.destLocation);
        }
      })
      .catch(e => {
        debug(e);
      })
  }

  const onChangeOriginLocation = (_, newValue) => {
    if (newValue) {
      dispatch({ type: FILTER_DEST_LOCATION, value: null });
      setFilteredDestLocations(bodLookupList.current, newValue, null);
    } else {
      setDestLocations(allDestLocations.current);
    }

    dispatch({ type: FILTER_ORIGIN_LOCATION, value: newValue });
  }

  useEffect(() => {
    if (reload) {
      retrieveOriginDestLocations();
      setReload(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  useEffect(() => {
    retrieveOriginDestLocations();

    return () => {
      setOriginLocations([]);
      setDestLocations([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const LoadingFilter = () => {
    return <FormControl size="small" sx={{ minWidth: 240, minHeight: "35px", mr: 1 }} key={"Loading"}>
      <InputLabel>Loading...</InputLabel>
    </FormControl>
  }

  return (
    <FormGroup key={"ori-dest-loc-autocomplete-group"} row sx={{ lex: "none", ...sx }}>
      {loading ? (
        <>
          <Autocomplete
            options={originLocations}
            getOptionLabel={option => option.name}
            value={filterValue.originLocation}
            onChange={onChangeOriginLocation}
            isOptionEqualToValue={(option, value) => option.locCd === value.locCd}
            size="small"
            sx={{ minWidth: 220, width: 240, mr: 1 }}
            renderInput={(params) => (
              <TextField {...params} label={t("originLocation")} />
            )}
          />
          <Autocomplete
            options={destLocations}
            getOptionLabel={option => option.name}
            value={filterValue.destLocation}
            onChange={(_, newValue) => dispatch({ type: FILTER_DEST_LOCATION, value: newValue })}
            isOptionEqualToValue={(option, value) => option.locCd === value.locCd}
            size="small"
            sx={{ minWidth: 220, width: 240, mr: 1 }}
            renderInput={(params) => (
              <TextField {...params} label={t("destLocation")} />
            )}
          />
        </>
      ) : (
        <>
          <LoadingFilter />
          <LoadingFilter />
        </>
      )}
    </FormGroup>
  )
}

export default React.memo(OriginDestLocationAutoComplete);